import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useVenues } from '../contexts/VenueContext';
import { differenceInDays, addDays } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiMapPin, FiUsers, FiCheck, FiClock } = FiIcons;

const BookingWidget = () => {
  const { clientId, venueId } = useParams();
  const { venues } = useVenues();
  
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(1);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Fallback mock venue if not found in context
  const venue = venues.find(v => v.id === venueId) || {
    id: venueId,
    ownerId: clientId,
    name: 'Luxury Downtown Apartment',
    type: 'apartment',
    location: {
      address: '123 Main St, New York, NY',
      coordinates: { lat: 40.7128, lng: -74.0060 }
    },
    photos: ['https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800'],
    facilities: ['WiFi', 'Kitchen', 'Air Conditioning', 'Parking'],
    maxCapacity: 4,
    pricePerNight: 150,
    pricingModel: 'per_unit', // Default mock
    occupancyRates: [],
    priceIncludesUtilities: false,
    selectedUtilities: ['electricity'],
    utilities: [
      {
        id: 'electricity',
        name: 'Electricity',
        priceType: 'per_day',
        billingBasis: 'per_unit',
        periods: [{ startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: 5 }]
      }
    ]
  };

  const calculateTotalCost = () => {
    if (!checkIn || !checkOut) return 0;
    const nights = differenceInDays(checkOut, checkIn);
    if (nights <= 0) return 0;

    // 1. Calculate Base Price
    let basePrice = 0;
    
    if (venue.pricingModel === 'occupancy_based' && venue.occupancyRates) {
      // Find exact price for guest count
      const rate = venue.occupancyRates.find(r => r.guests === guests);
      if (rate) {
        basePrice = rate.price * nights;
      } else {
         // Fallback if not found (shouldn't happen with UI limits)
         const maxRate = venue.occupancyRates[venue.occupancyRates.length - 1];
         basePrice = (maxRate ? maxRate.price : venue.pricePerNight) * nights;
      }
    } else {
      // Flat rate per unit
      basePrice = venue.pricePerNight * nights;
    }

    let total = basePrice;

    // 2. Calculate Utilities
    if (!venue.priceIncludesUtilities && venue.selectedUtilities) {
      venue.selectedUtilities.forEach(utilityId => {
        const utility = venue.utilities.find(u => u.id === utilityId);
        if (utility) {
          const cost = utility.periods[0]?.costPerDay || 0;
          let utilityTotal = cost;

          // Apply per-person multiplier if applicable
          if (utility.billingBasis === 'per_person') {
            utilityTotal = cost * guests;
          }

          // Apply frequency multiplier
          if (utility.priceType === 'per_booking') {
            total += utilityTotal;
          } else {
            total += utilityTotal * nights;
          }
        }
      });
    }

    return total;
  };

  const handleBooking = (e) => {
    e.preventDefault();
    alert(`Booking submitted for ${venue.name}!\nClient ID: ${clientId}\nCheck-in: ${checkIn.toLocaleDateString()}\nCheck-out: ${checkOut.toLocaleDateString()}\nGuests: ${guests}\nTotal: €${calculateTotalCost()}`);
    setShowBookingForm(false);
    setBookingData({ firstName: '', lastName: '', email: '', phone: '' });
    setCheckIn(null);
    setCheckOut(null);
  };

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;
  const totalCost = calculateTotalCost();

  // Determine base price display
  const getBasePriceDisplay = () => {
    if (venue.pricingModel === 'occupancy_based') {
       const rate = venue.occupancyRates?.find(r => r.guests === guests);
       return rate ? rate.price : venue.pricePerNight;
    }
    return venue.pricePerNight;
  };

  const currentBasePrice = getBasePriceDisplay();

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="relative h-48">
        <img src={venue.photos[0]} alt={venue.name} className="w-full h-full object-cover" />
        <div className="absolute top-4 right-4 bg-white px-2 py-1 rounded-full text-sm font-semibold text-gray-900">
          €{currentBasePrice}/night
        </div>
      </div>
      
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{venue.name}</h2>
          <div className="flex items-center text-gray-600 mb-2">
            <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2" />
            <span className="text-sm">{venue.location.address}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {venue.facilities.slice(0, 3).map((facility, index) => (
              <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                {facility}
              </span>
            ))}
          </div>
        </div>

        {!showBookingForm ? (
          <form className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="relative z-20">
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in</label>
                <div className="relative">
                  <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                  <DatePicker
                    selected={checkIn}
                    onChange={(date) => {
                      setCheckIn(date);
                      if (checkOut && date >= checkOut) {
                        setCheckOut(null);
                      }
                    }}
                    selectsStart
                    startDate={checkIn}
                    endDate={checkOut}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/mm/yyyy"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm cursor-pointer"
                    wrapperClassName="w-full"
                  />
                </div>
              </div>
              <div className="relative z-20">
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out</label>
                <div className="relative">
                  <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                  <DatePicker
                    selected={checkOut}
                    onChange={(date) => setCheckOut(date)}
                    selectsEnd
                    startDate={checkIn}
                    endDate={checkOut}
                    minDate={checkIn ? addDays(checkIn, 1) : new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/mm/yyyy"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm cursor-pointer"
                    wrapperClassName="w-full"
                    disabled={!checkIn}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Guests</label>
              <div className="relative">
                <SafeIcon icon={FiUsers} className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <select
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  {[...Array(venue.maxCapacity || 6)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1} guest{i > 0 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            {nights > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-gray-50 rounded-lg p-4 space-y-2"
              >
                <div className="flex justify-between text-sm">
                  <span>
                    €{currentBasePrice} × {nights} nights
                    {venue.pricingModel === 'occupancy_based' && <span className="text-xs text-gray-500 block">({guests} guests rate)</span>}
                  </span>
                  <span>
                    €{currentBasePrice * nights}
                  </span>
                </div>
                
                {!venue.priceIncludesUtilities && venue.selectedUtilities?.length > 0 && (
                  <>
                    {venue.selectedUtilities.map(utilityId => {
                      const utility = venue.utilities.find(u => u.id === utilityId);
                      if (!utility) return null;
                      
                      const cost = utility.periods[0]?.costPerDay || 0;
                      const isPerBooking = utility.priceType === 'per_booking';
                      const isPerPerson = utility.billingBasis === 'per_person';
                      
                      let displayCost = cost;
                      if (isPerPerson) displayCost = cost * guests;
                      if (!isPerBooking) displayCost = displayCost * nights;

                      return (
                        <div key={utilityId} className="flex justify-between text-sm text-gray-600">
                          <span>
                            {utility.name} 
                            {isPerPerson ? ` (${guests} guests)` : ''}
                            {isPerBooking ? ' (One-time)' : ` × ${nights} nights`}
                          </span>
                          <span>€{displayCost}</span>
                        </div>
                      );
                    })}
                  </>
                )}
                
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span>€{totalCost}</span>
                </div>
              </motion.div>
            )}

            <button
              type="button"
              onClick={() => setShowBookingForm(true)}
              disabled={!checkIn || !checkOut || nights <= 0}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-semibold transition-colors"
            >
              {nights > 0 ? `Book Now - €${totalCost}` : 'Select Dates'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleBooking} className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiCheck} className="h-4 w-4 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-900">Booking Summary</span>
              </div>
              <div className="text-sm text-blue-800">
                <p>{checkIn.toLocaleDateString('en-GB')} - {checkOut.toLocaleDateString('en-GB')}</p>
                <p>{nights} nights • {guests} guests • €{totalCost} total</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  required
                  value={bookingData.firstName}
                  onChange={(e) => setBookingData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  required
                  value={bookingData.lastName}
                  onChange={(e) => setBookingData(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={bookingData.email}
                onChange={(e) => setBookingData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                required
                value={bookingData.phone}
                onChange={(e) => setBookingData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowBookingForm(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold transition-colors text-sm"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-semibold transition-colors text-sm inline-flex items-center justify-center"
              >
                <SafeIcon icon={FiClock} className="mr-2 h-4 w-4" />
                Confirm Booking
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingWidget;